import olMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from "ol/source/Vector";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Circle from "ol/style/Circle";
import Stroke from "ol/style/Stroke";
import Feature from "ol/Feature";
import { MapBrowserEvent, Overlay } from "ol";
import { Draw } from "ol/interaction";
import { DrawEvent } from "ol/interaction/Draw";
import * as olSphere from 'ol/sphere';
import LineString from "ol/geom/LineString";
import { unByKey } from 'ol/Observable';
import GeometryType from "ol/geom/GeometryType";
import OverlayPositioning from "ol/OverlayPositioning";

/**
 * The distance measuring tool: draw interaction, live measure tooltip and the
 * "click to start drawing" help tooltip.
 */
export class MeasureTool {

    private _sketch: Feature = null;
    private _draw: Draw = null;
    private _helpTooltipElement: HTMLDivElement;
    private _helpTooltip: Overlay;
    private _measureLayer: VectorLayer;
    private _measureInKm: boolean = true;
    private _measureTooltipElement: HTMLDivElement;
    private _measureTooltip: Overlay;
    private _active: boolean = false;

    public constructor(private _map: olMap) {
        const self = this;

        this._map.on('pointermove', this.handlePointerMove);

        this._map.getViewport().addEventListener('mouseout', function (this: HTMLElement, ev: MouseEvent) {
            if (self._helpTooltipElement) {
                self._helpTooltipElement.classList.add('hidden');
            }
        });
    }

    public activate = (inKm: boolean): void => {
        this._measureInKm = inKm;
        this._active = true;
        this.addMeasureInteraction();
    }

    public deactivate = (): void => {
        this._active = false;
        this.removeMeasureInteraction();
    }

    private addMeasureInteraction = (): void => {
        if (this._draw !== null) {
            //already initiated
            return;
        }
        let self = this;
        let source = new VectorSource();

        this._measureLayer = new VectorLayer({
            source: source,
            style: new Style({
                fill: new Fill({
                    color: 'rgba(255, 0, 0, 1)'
                }),
                stroke: new Stroke({
                    color: '#ffcc33',
                    width: 2
                }),
                image: new Circle({
                    radius: 7,
                    fill: new Fill({
                        color: '#ffcc33'
                    })
                })
            })
        });

        this._measureLayer.set("id", "measure-tool", true);
        this._map.addLayer(this._measureLayer);
        this._measureLayer.setVisible(true);

        let formatLength = function (line) {
            let length = olSphere.getLength(line);
            let output;

            if (self._measureInKm) {
                if (length > 100) {
                    output = (Math.round(length / 1000 * 100) / 100) +
                        ' ' + 'km';
                } else {
                    output = (Math.round(length * 100) / 100) +
                        ' ' + 'm';
                }
            } else {
                //miles
                output = (Math.round((length * 0.0006213712) * 100) / 100) + ' mile(s)';
            }

            return output;
        };

        this._draw = new Draw({
            source: source,
            type: GeometryType.LINE_STRING,
            style: new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.2)'
                }),
                stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.5)',
                    lineDash: [10, 10],
                    width: 2
                }),
                image: new Circle({
                    radius: 5,
                    stroke: new Stroke({
                        color: 'rgba(0, 0, 0, 0.7)'
                    }),
                    fill: new Fill({
                        color: 'rgba(255, 255, 255, 0.2)'
                    })
                })
            })
        });

        this._map.addInteraction(this._draw);
        this.createMeasureTooltip();
        this.createHelpTooltip();

        let listener;
        this._draw.on('drawstart',
            function (evt: DrawEvent) {

                if (!self._active) {
                    //nothing to do
                    return;
                }

                // set sketch
                self._sketch = evt.feature;

                let tooltipCoord;

                listener = self._sketch.getGeometry().on('change', function (evt) {
                    let geom = evt.target;
                    let output;
                    if (geom instanceof LineString) {
                        output = formatLength(geom);
                        tooltipCoord = geom.getLastCoordinate();
                    }
                    self._measureTooltipElement.innerHTML = output;
                    self._measureTooltip.setPosition(tooltipCoord);
                });
            });

        this._draw.on('drawend',
            function () {
                self._measureTooltipElement.className = 'tooltip tooltip-static';
                self._measureTooltip.setOffset([0, -7]);
                // unset sketch
                self._sketch = null;
                // unset tooltip so that a new one can be created
                self._measureTooltipElement = null;
                self.createMeasureTooltip();
                unByKey(listener);
            });
    }

    private removeMeasureInteraction = (): void => {
        if (this._measureLayer) {
            this._measureLayer.setVisible(false);
        }

        if (this._draw) {
            this._map.removeInteraction(this._draw);
        }

        this._draw = null;

        $('.tooltip-static').remove();

        if (this._measureTooltip) {
            this._map.removeOverlay(this._measureTooltip);
        }

        if (this._measureTooltipElement) {
            this._measureTooltipElement.className = 'tooltip tooltip-measure hidden';
            this._measureTooltipElement.parentNode.removeChild(this._measureTooltipElement);
        }

        if (this._helpTooltipElement) {
            this._helpTooltipElement.parentNode.removeChild(this._helpTooltipElement);
        }
    }

    private handlePointerMove = (evt: MapBrowserEvent): void => {
        if (evt.dragging) {
            return;
        }

        if (!this._active || !this._helpTooltipElement) {
            //nothing to do
            return;
        }

        let helpMsg = 'Click to start drawing';
        if (this._sketch !== null) {
            helpMsg = "Double click to finish drawing";
        }

        this._helpTooltipElement.innerHTML = helpMsg;
        this._helpTooltip.setPosition(evt.coordinate);
        this._helpTooltipElement.classList.remove('hidden');
    }

    private createHelpTooltip = (): void => {
        if (this._helpTooltipElement && this._helpTooltipElement.parentNode) {
            this._helpTooltipElement.parentNode.removeChild(this._helpTooltipElement);
        }
        this._helpTooltipElement = document.createElement('div');
        this._helpTooltipElement.className = 'tooltip hidden';
        this._helpTooltipElement.innerHTML = 'Shift click to start drawing';
        this._helpTooltip = new Overlay({
            element: this._helpTooltipElement,
            offset: [15, 0],
            positioning: OverlayPositioning.CENTER_LEFT
        });
        this._map.addOverlay(this._helpTooltip);
    }

    private createMeasureTooltip = (): void => {
        if (this._measureTooltipElement && this._measureTooltipElement.parentNode) {
            this._measureTooltipElement.parentNode.removeChild(this._measureTooltipElement);
        }
        this._measureTooltipElement = document.createElement('div');
        this._measureTooltipElement.className = 'tooltip tooltip-measure hidden';
        this._measureTooltip = new Overlay({
            element: this._measureTooltipElement,
            offset: [0, -15],
            positioning: OverlayPositioning.BOTTOM_CENTER,
            autoPan: true,
            autoPanMargin: 150
        });
        this._map.addOverlay(this._measureTooltip);
    }
}
